/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  UcApiError,
  fetchDepartmentTreeV2,
  type UcDeptTreeNode,
} from '@coze-foundation/account-adapter';
import { I18n } from '@coze-arch/i18n';
import { Input, Modal, Spin, Tag, Toast, Tree } from '@coze-arch/coze-design';
import { isApiError } from '@coze-arch/bot-http';

import { PublishModalFooter } from './publish-modal-footer';

import styles from './index.module.less';

export interface DeptTreeModalOpenOptions {
  values?: string[];
  multiple?: boolean;
  canEdit?: boolean;
  title?: string;
}

export interface DeptTreeModalRef {
  open: (options?: DeptTreeModalOpenOptions) => void;
}

export interface DeptTreeModalProps {
  onConfirm?: (nodes: UcDeptTreeNode[], appointData: string[]) => void;
}

interface TreeNodeData {
  label: ReactNode;
  value: string;
  key: string;
  children?: TreeNodeData[];
}

function filterTree(
  nodes: UcDeptTreeNode[],
  keyword: string,
): UcDeptTreeNode[] {
  const q = keyword.trim().toLowerCase();
  if (!q) {
    return nodes;
  }
  const walk = (list: UcDeptTreeNode[]): UcDeptTreeNode[] => {
    const result: UcDeptTreeNode[] = [];
    for (const node of list) {
      const children = node.children ? walk(node.children) : [];
      const nameMatch = node.departmentName?.toLowerCase().includes(q);
      if (nameMatch || children.length > 0) {
        result.push({
          ...node,
          children: children.length > 0 ? children : node.children,
        });
      }
    }
    return result;
  };
  return walk(nodes);
}

function toTreeData(nodes: UcDeptTreeNode[]): TreeNodeData[] {
  return nodes.map(node => ({
    key: node.departmentId,
    value: node.departmentId,
    label: (
      <span className={styles.deptTreeNodeLabel}>
        <span>{node.departmentName}</span>
        {node.departmentType === 1 ? (
          <Tag size="small" color="orange">
            {I18n.t('abr_publish_dept_company')}
          </Tag>
        ) : null}
        {node.departmentType === 2 ? (
          <Tag size="small" color="blue">
            {I18n.t('abr_publish_dept_department')}
          </Tag>
        ) : null}
      </span>
    ),
    children: node.children?.length ? toTreeData(node.children) : undefined,
  }));
}

function collectNodesByIds(
  nodes: UcDeptTreeNode[],
  ids: Set<string>,
  out: UcDeptTreeNode[],
) {
  for (const node of nodes) {
    if (ids.has(node.departmentId)) {
      out.push(node);
    }
    if (node.children?.length) {
      collectNodesByIds(node.children, ids, out);
    }
  }
}

function normalizeCheckedKeys(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(item => {
      if (typeof item === 'string') {
        return item.trim();
      }
      if (item && typeof item === 'object') {
        const record = item as { key?: string; value?: string };
        return (record.key ?? record.value ?? '').trim();
      }
      return '';
    })
    .filter(Boolean);
}

export const DeptTreeModal = forwardRef<DeptTreeModalRef, DeptTreeModalProps>(
  ({ onConfirm }, ref) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [treeSource, setTreeSource] = useState<UcDeptTreeNode[]>([]);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [canEdit, setCanEdit] = useState(true);
    const [title, setTitle] = useState('');

    const loadTree = useCallback(async () => {
      setLoading(true);
      try {
        const tree = await fetchDepartmentTreeV2();
        setTreeSource(Array.isArray(tree) ? tree : []);
      } catch (error) {
        setTreeSource([]);
        let msg = I18n.t('abr_publish_dept_load_failed');
        if (error instanceof UcApiError) {
          msg = error.message;
        } else if (isApiError(error) && error.msg) {
          msg = error.msg;
        }
        Toast.error(msg);
      } finally {
        setLoading(false);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      open: (options?: DeptTreeModalOpenOptions) => {
        setTitle(options?.title ?? I18n.t('abr_publish_dept_modal_title'));
        setCanEdit(options?.canEdit !== false);
        setCheckedKeys(options?.values ?? []);
        setKeyword('');
        setVisible(true);
        void loadTree();
      },
    }));

    const filteredTree = useMemo(
      () => filterTree(treeSource, keyword),
      [treeSource, keyword],
    );
    const treeData = useMemo(() => toTreeData(filteredTree), [filteredTree]);

    const handleOk = () => {
      const idSet = new Set(checkedKeys);
      const selected: UcDeptTreeNode[] = [];
      collectNodesByIds(treeSource, idSet, selected);
      onConfirm?.(selected, checkedKeys);
      setVisible(false);
    };

    return (
      <Modal
        title={title}
        visible={visible}
        width={560}
        footer={
          <PublishModalFooter
            cancelText={I18n.t('Cancel')}
            okText={I18n.t('Confirm')}
            onCancel={() => setVisible(false)}
            onOk={handleOk}
          />
        }
        onCancel={() => setVisible(false)}
      >
        <div className={styles.deptTreeBody}>
          <Input
            className={styles.deptTreeSearch}
            placeholder={I18n.t('abr_publish_dept_search_placeholder')}
            value={keyword}
            onChange={v => setKeyword(String(v))}
            disabled={!canEdit}
          />
          {loading ? (
            <div className={styles.deptTreeLoading}>
              <Spin />
            </div>
          ) : (
            <div className={styles.deptTreeTreeWrap}>
              <Tree
                treeData={treeData}
                value={checkedKeys}
                multiple
                checkRelation="unRelated"
                disabled={!canEdit}
                onChange={value => {
                  if (!canEdit) {
                    return;
                  }
                  setCheckedKeys(normalizeCheckedKeys(value));
                }}
              />
            </div>
          )}
        </div>
      </Modal>
    );
  },
);

DeptTreeModal.displayName = 'DeptTreeModal';
